/* ================================================================
   EAGLE RAY — Shopify homepage behaviors.
   Ported from UI/script.js. See CLAUDE.md for rules.

   Architecture notes:
   - Each initializer is scoped (accepts an optional root) and idempotent
     (skips elements already marked with data-eagleray-init).
   - Listens for Shopify's editor lifecycle events so live setting changes
     re-init JS on the freshly rendered section. Without this, the editor
     preview goes blank for any section using .eagleray-reveal because the
     new DOM never receives the .is-visible class.
   - In Shopify.designMode, the reveal animation is bypassed entirely so
     re-rendered sections are immediately visible without scroll.
================================================================ */
(function () {
  'use strict';

  if (window.__eaglerayBootInstalled) return;
  window.__eaglerayBootInstalled = true;

  var INIT_ATTR = 'data-eagleray-init';
  var inDesignMode = !!(window.Shopify && window.Shopify.designMode);

  function isMobile() { return window.innerWidth < 768; }
  function notInited(el) { return el && !el.hasAttribute(INIT_ATTR); }
  function markInited(el) { if (el) el.setAttribute(INIT_ATTR, ''); }
  function rootOf(scope) { return scope || document; }

  // --------------------------------------------------------------
  // Scroll reveal
  // In design mode, mark everything visible immediately — Shopify's
  // editor swaps in fresh HTML on every setting change, and a fade-on-
  // scroll observer would leave the new DOM at opacity:0 indefinitely.
  // --------------------------------------------------------------
  function initReveal(scope) {
    var els = rootOf(scope).querySelectorAll('.eagleray-reveal');
    if (!els.length) return;
    if (inDesignMode) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var pending = [];
    els.forEach(function (el) {
      if (notInited(el)) { markInited(el); pending.push(el); }
    });
    if (!pending.length) return;
    var observer = new IntersectionObserver(function (entries) {
      var visible = entries.filter(function (e) { return e.isIntersecting; });
      visible.forEach(function (entry, batchIndex) {
        setTimeout(function () { entry.target.classList.add('is-visible'); }, batchIndex * 60);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -48px 0px' });
    pending.forEach(function (el) { observer.observe(el); });
  }

  // --------------------------------------------------------------
  // Carousel dots (mobile only, auto-rebuild on resize)
  // --------------------------------------------------------------
  function setupCarousel(track, dotsContainer) {
    if (!track || !dotsContainer) return;
    var cards = track.children;
    if (!cards.length) return;

    function build() {
      dotsContainer.innerHTML = '';
      if (!isMobile()) return;
      for (var i = 0; i < cards.length; i++) {
        var dot = document.createElement('span');
        dot.className = 'eagleray-dots__dot' + (i === 0 ? ' is-active' : '');
        dotsContainer.appendChild(dot);
      }
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var index = Array.prototype.indexOf.call(cards, entry.target);
          var dots = dotsContainer.querySelectorAll('.eagleray-dots__dot');
          dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
        });
      }, { root: track, threshold: 0.6 });
      Array.prototype.forEach.call(cards, function (card) { observer.observe(card); });
    }

    build();
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 250);
    });
  }

  function initCarousels(scope) {
    rootOf(scope).querySelectorAll('[data-eagleray-carousel]').forEach(function (track) {
      if (!notInited(track)) return;
      markInited(track);
      var dots = document.querySelector('[data-eagleray-dots="' + track.dataset.eaglerayCarousel + '"]');
      setupCarousel(track, dots);
    });
  }

  // --------------------------------------------------------------
  // Hero slideshow (manual; no autoplay per brand rules)
  // --------------------------------------------------------------
  function setupHeroSlideshow(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-eagleray-hero-slide]'));
    if (!slides.length) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-eagleray-hero-dot]'));
    var prev = root.querySelector('[data-eagleray-hero-prev]');
    var next = root.querySelector('[data-eagleray-hero-next]');
    var progress = root.querySelector('[data-eagleray-hero-progress]');
    var progressFill = root.querySelector('[data-eagleray-hero-progress-fill]');
    var index = 0;

    function show(i) {
      var n = slides.length;
      index = ((i % n) + n) % n;
      slides.forEach(function (s, k) { s.classList.toggle('is-active', k === index); });
      dots.forEach(function (d, k) {
        var active = k === index;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (progressFill) progressFill.style.width = ((index + 1) / n * 100) + '%';
      if (progress) progress.setAttribute('aria-valuenow', index + 1);
    }

    show(0);

    if (slides.length < 2) return;

    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        var i = parseInt(d.getAttribute('data-index'), 10);
        if (!isNaN(i)) show(i);
      });
    });

    var inView = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { inView = e.isIntersecting; });
    }, { threshold: 0.4 });
    io.observe(root);

    document.addEventListener('keydown', function (e) {
      if (!inView) return;
      if (e.key === 'ArrowRight') show(index + 1);
      if (e.key === 'ArrowLeft') show(index - 1);
    });

    var startX = 0, startY = 0, tracking = false;
    root.addEventListener('touchstart', function (e) {
      if (!e.touches || !e.touches[0]) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (!tracking) return;
      tracking = false;
      var t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) return;
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) show(index + 1); else show(index - 1);
    }, { passive: true });

    root.__eaglerayHeroShow = show;
  }

  function initHeroSlideshow(scope) {
    rootOf(scope).querySelectorAll('[data-eagleray-hero-slideshow]').forEach(function (root) {
      if (!notInited(root)) return;
      markInited(root);
      setupHeroSlideshow(root);
    });
  }

  // --------------------------------------------------------------
  // Reel autoplay when visible
  // --------------------------------------------------------------
  function initReelAutoplay(scope) {
    var reels = rootOf(scope).querySelectorAll('.eagleray-reel');
    if (!reels.length) return;
    var pending = [];
    reels.forEach(function (r) { if (notInited(r)) { markInited(r); pending.push(r); } });
    if (!pending.length) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target.querySelector('.eagleray-reel__video');
        if (!video) return;
        if (entry.isIntersecting) {
          video.play().catch(function () { });
          entry.target.classList.add('is-playing');
        } else {
          video.pause();
          entry.target.classList.remove('is-playing');
        }
      });
    }, { threshold: 0.5 });
    pending.forEach(function (reel) { observer.observe(reel); });
  }

  // --------------------------------------------------------------
  // Reel fullscreen modal
  // Document-level keydown is bound once at module load (see bottom),
  // not per init, so re-renders don't accumulate handlers.
  // --------------------------------------------------------------
  var reelModalState = { modal: null, video: null, info: null, idx: 0, cards: [] };

  function reelModalLoad(i) {
    var s = reelModalState;
    var card = s.cards[i];
    if (!card) return;
    var source = card.querySelector('source');
    var cardVideo = card.querySelector('.eagleray-reel__video');
    if (source && s.video) {
      s.video.src = source.src;
      s.video.poster = cardVideo ? cardVideo.getAttribute('poster') || '' : '';
      s.video.load();
    }
    var overlay = card.querySelector('.eagleray-reel__overlay');
    if (overlay && s.info) s.info.innerHTML = overlay.innerHTML;
    var prev = s.modal.querySelector('.eagleray-reel-modal__prev');
    var next = s.modal.querySelector('.eagleray-reel-modal__next');
    if (prev) prev.style.display = i === 0 ? 'none' : '';
    if (next) next.style.display = i === s.cards.length - 1 ? 'none' : '';
  }
  function reelModalOpen(i) {
    var s = reelModalState;
    if (!s.modal || !s.video) return;
    s.idx = i;
    reelModalLoad(i);
    s.modal.classList.add('is-open');
    s.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    s.video.play().catch(function () { });
  }
  function reelModalClose() {
    var s = reelModalState;
    if (!s.modal || !s.video) return;
    s.modal.classList.remove('is-open');
    s.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    s.video.pause();
    s.video.removeAttribute('src');
    s.video.load();
  }
  function reelModalGo(delta) {
    var s = reelModalState;
    var next = s.idx + delta;
    if (next < 0 || next >= s.cards.length) return;
    s.idx = next;
    reelModalLoad(next);
    if (s.video) s.video.play().catch(function () { });
  }

  function initReelModal(scope) {
    var modal = document.getElementById('eagleray-reel-modal');
    if (!modal) return;
    reelModalState.modal = modal;
    reelModalState.video = modal.querySelector('.eagleray-reel-modal__video');
    reelModalState.info = modal.querySelector('.eagleray-reel-modal__info');
    reelModalState.cards = Array.prototype.slice.call(document.querySelectorAll('.eagleray-reel'));

    if (notInited(modal)) {
      markInited(modal);
      var closeBtn = modal.querySelector('.eagleray-reel-modal__close');
      var prevBtn = modal.querySelector('.eagleray-reel-modal__prev');
      var nextBtn = modal.querySelector('.eagleray-reel-modal__next');
      if (closeBtn) closeBtn.addEventListener('click', reelModalClose);
      if (prevBtn) prevBtn.addEventListener('click', function () { reelModalGo(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { reelModalGo(1); });

      var startX = 0;
      modal.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      modal.addEventListener('touchend', function (e) {
        var diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) reelModalGo(diff > 0 ? 1 : -1);
      }, { passive: true });
    }

    rootOf(scope).querySelectorAll('.eagleray-reel').forEach(function (card) {
      if (!notInited(card)) return; // already marked by initReelAutoplay or here — fine, skip
      // No re-mark here — autoplay already marked. Just attach click.
      if (card.__eaglerayReelClickBound) return;
      card.__eaglerayReelClickBound = true;
      card.addEventListener('click', function () {
        reelModalState.cards = Array.prototype.slice.call(document.querySelectorAll('.eagleray-reel'));
        var i = reelModalState.cards.indexOf(card);
        if (i >= 0) reelModalOpen(i);
      });
    });
  }

  // --------------------------------------------------------------
  // Newsletter inline feedback
  // --------------------------------------------------------------
  function initNewsletter(scope) {
    rootOf(scope).querySelectorAll('.eagleray-footer__newsletter-form').forEach(function (form) {
      if (!notInited(form)) return;
      markInited(form);
      form.addEventListener('submit', function () {
        var note = form.querySelector('[data-eagleray-newsletter-note]') ||
          (form.parentElement && form.parentElement.querySelector('[data-eagleray-newsletter-note]'));
        if (!note) return;
        var msg = note.dataset.successMessage || 'Thank you. You\'re on the list.';
        setTimeout(function () {
          if (!note.textContent.trim()) note.textContent = msg;
          setTimeout(function () { note.textContent = ''; }, 4000);
        }, 50);
      });
    });
  }

  // --------------------------------------------------------------
  // Announcement bar offset → CSS var.
  // Idempotent: re-running re-points to whatever .announcement-bar
  // exists now. Listeners are bound once and reused.
  // --------------------------------------------------------------
  var announceState = { bar: null, height: 0, scrollBound: false, observer: null };

  function announceMeasure() {
    if (announceState.bar) {
      announceState.height = Math.ceil(announceState.bar.getBoundingClientRect().height);
    }
  }
  function announceUpdate() {
    var root = document.documentElement;
    if (!announceState.bar) {
      root.style.setProperty('--eagleray-announcement-h', '0px');
      return;
    }
    var rect = announceState.bar.getBoundingClientRect();
    var visible = Math.max(0, Math.min(announceState.height, rect.bottom));
    root.style.setProperty('--eagleray-announcement-h', visible + 'px');
  }
  function initAnnouncementOffset() {
    var bar = document.querySelector('.announcement-bar');
    announceState.bar = bar;
    if (announceState.observer) {
      announceState.observer.disconnect();
      announceState.observer = null;
    }
    if (!bar) {
      document.documentElement.style.setProperty('--eagleray-announcement-h', '0px');
      return;
    }
    announceMeasure();
    announceUpdate();
    if ('ResizeObserver' in window) {
      announceState.observer = new ResizeObserver(function () { announceMeasure(); announceUpdate(); });
      announceState.observer.observe(bar);
    }
    if (!announceState.scrollBound) {
      announceState.scrollBound = true;
      window.addEventListener('scroll', announceUpdate, { passive: true });
      window.addEventListener('resize', function () { announceMeasure(); announceUpdate(); });
    }
  }

  // --------------------------------------------------------------
  // Nav height → CSS var.
  // Used by .eagleray-nav--transparent to pull the next flow sibling
  // (hero) up behind the transparent header without breaking sticky.
  // Measures the un-shrunk height by ignoring the .is-scrolled state.
  // --------------------------------------------------------------
  var navState = { nav: null, observer: null, resizeBound: false };
  function navMeasure() {
    if (!navState.nav) return;
    var h = Math.ceil(navState.nav.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--eagleray-nav-h', h + 'px');
  }
  function initNavOffset() {
    var nav = document.querySelector('[data-eagleray-nav]');
    navState.nav = nav;
    if (navState.observer) {
      navState.observer.disconnect();
      navState.observer = null;
    }
    if (!nav) {
      document.documentElement.style.setProperty('--eagleray-nav-h', '0px');
      return;
    }
    navMeasure();
    if ('ResizeObserver' in window) {
      navState.observer = new ResizeObserver(navMeasure);
      navState.observer.observe(nav);
    }
    if (!navState.resizeBound) {
      navState.resizeBound = true;
      window.addEventListener('resize', navMeasure);
    }
  }

  // --------------------------------------------------------------
  // Header: scroll state + mobile menu toggle
  // --------------------------------------------------------------
  function initHeader(scope) {
    var nav = rootOf(scope).querySelector('[data-eagleray-nav]') || document.querySelector('[data-eagleray-nav]');
    if (nav && notInited(nav)) {
      markInited(nav);
      var hero = document.querySelector('.eagleray-hero');
      if (hero) {
        var observer = new IntersectionObserver(function (entries) {
          nav.classList.toggle('is-scrolled', !entries[0].isIntersecting);
        }, { threshold: 0.05 });
        observer.observe(hero);
      } else {
        nav.classList.add('is-scrolled');
      }

        if (!document.__eaglerayStickyScrollBound) {
          document.__eaglerayStickyScrollBound = true;
          var lastY = window.pageYOffset || 0;
          var ticking = false;
          var DELTA = 6;
          function onStickyScroll() {
            var activeNav = document.querySelector('[data-eagleray-nav][data-sticky-mode="scroll-up"]');
            if (!activeNav) {
              ticking = false;
              return;
            }
            var y = window.pageYOffset || 0;
            var threshold = activeNav.offsetHeight + 40;
            if (y <= threshold) {
              activeNav.removeAttribute('data-sticky-state');
            } else if (y - lastY > DELTA) {
              activeNav.setAttribute('data-sticky-state', 'hidden');
            } else if (lastY - y > DELTA) {
              activeNav.removeAttribute('data-sticky-state');
            }
            lastY = y;
            ticking = false;
          }
          window.addEventListener('scroll', function () {
            if (!ticking) {
              window.requestAnimationFrame(onStickyScroll);
              ticking = true;
            }
          }, { passive: true });
        }
    }

    var searchBtn = rootOf(scope).querySelector('[data-eagleray-search-toggle]') || document.querySelector('[data-eagleray-search-toggle]');
    if (searchBtn && notInited(searchBtn)) {
      markInited(searchBtn);
      searchBtn.addEventListener('click', function () {
        var modal = document.getElementById('search-modal');
        if (!modal) return;
        if (typeof modal.showDialog === 'function') {
          modal.showDialog();
          return;
        }
        var native = modal.querySelector('dialog');
        if (native && typeof native.showModal === 'function') native.showModal();
      });
    }

    var toggle = document.querySelector('[data-eagleray-menu-toggle]');
    var menu = document.querySelector('[data-eagleray-mobile-menu]');
    if (!toggle || !menu) return;
    if (!notInited(toggle)) return;
    markInited(toggle);

    function openMenu() {
      toggle.classList.add('is-active');
      toggle.setAttribute('aria-expanded', 'true');
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closeMenu() {
      toggle.classList.remove('is-active');
      toggle.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', function () {
      menu.classList.contains('is-open') ? closeMenu() : openMenu();
    });
    menu.querySelectorAll('[data-eagleray-mobile-menu-link]').forEach(function (link) {
      link.addEventListener('click', closeMenu);
    });
    // Close button inside mobile menu header
    var closeBtn = menu.querySelector('[data-eagleray-menu-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeMenu);
    }
  }

  // --------------------------------------------------------------
  // Mega menu: desktop hover + mobile accordion
  // --------------------------------------------------------------
  function initMegaMenu(scope) {
    // Desktop hover menus
    rootOf(scope).querySelectorAll('[data-eagleray-mega]').forEach(function (mega) {
      if (!notInited(mega)) return;
      markInited(mega);

      var trigger = mega.querySelector('[data-eagleray-mega-trigger]');
      var panel = mega.querySelector('[data-eagleray-mega-panel]');
      if (!trigger || !panel) return;

      var openTimer = null;
      var closeTimer = null;
      var OPEN_DELAY = 80;
      var CLOSE_DELAY = 200;

      function megaOpen() {
        clearTimeout(closeTimer);
        // Close any other open megas
        document.querySelectorAll('[data-eagleray-mega].is-open').forEach(function (other) {
          if (other !== mega) {
            other.classList.remove('is-open');
            var otherTrigger = other.querySelector('[data-eagleray-mega-trigger]');
            if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
          }
        });
        mega.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
      function megaClose() {
        clearTimeout(openTimer);
        mega.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }

      mega.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        openTimer = setTimeout(megaOpen, OPEN_DELAY);
      });
      mega.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
        closeTimer = setTimeout(megaClose, CLOSE_DELAY);
      });

      // Keyboard: open on focus, close on blur outside
      trigger.addEventListener('focus', megaOpen);
      mega.addEventListener('focusout', function (e) {
        if (!mega.contains(e.relatedTarget)) megaClose();
      });

      // Allow clicking trigger to navigate (it's a real link)
      // but also let it toggle the menu if keyboard users press Enter
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          megaClose();
          trigger.focus();
        }
      });
    });

    // Click outside to close all megas
    if (!document.__eaglerayMegaOutsideBound) {
      document.__eaglerayMegaOutsideBound = true;
      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-eagleray-mega]')) {
          document.querySelectorAll('[data-eagleray-mega].is-open').forEach(function (m) {
            m.classList.remove('is-open');
            var t = m.querySelector('[data-eagleray-mega-trigger]');
            if (t) t.setAttribute('aria-expanded', 'false');
          });
        }
      });
    }

    // Mobile accordion
    rootOf(scope).querySelectorAll('[data-eagleray-mobile-accordion]').forEach(function (group) {
      if (!notInited(group)) return;
      markInited(group);

      var btn = group.querySelector('[data-eagleray-mobile-accordion-trigger]');
      var panel = group.querySelector('[data-eagleray-mobile-accordion-panel]');
      if (!btn || !panel) return;

      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        panel.classList.toggle('is-open', !isOpen);
      });
    });
  }

  // --------------------------------------------------------------
  // Category nav: scroll the active link into view horizontally
  // --------------------------------------------------------------
  function initCategoryNav(scope) {
    rootOf(scope).querySelectorAll('[data-eagleray-category-nav]').forEach(function (nav) {
      if (!notInited(nav)) return;
      markInited(nav);
      var scroller = nav.querySelector('[data-eagleray-category-nav-scroller]');
      if (!scroller) return;
      var active = scroller.querySelector('.eagleray-category-nav__link.is-active');
      if (!active) return;
      var target = active.offsetLeft - (scroller.clientWidth / 2) + (active.offsetWidth / 2);
      scroller.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
    });
  }

  // --------------------------------------------------------------
  // Module-level document handlers (bound once, not per init)
  // --------------------------------------------------------------
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (reelModalState.modal && reelModalState.modal.classList.contains('is-open')) {
      reelModalClose();
      return;
    }
    var menu = document.querySelector('[data-eagleray-mobile-menu]');
    var toggle = document.querySelector('[data-eagleray-menu-toggle]');
    if (menu && menu.classList.contains('is-open') && toggle) {
      toggle.classList.remove('is-active');
      toggle.setAttribute('aria-expanded', 'false');
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  });
  document.addEventListener('keydown', function (e) {
    if (!reelModalState.modal || !reelModalState.modal.classList.contains('is-open')) return;
    if (e.key === 'ArrowRight') reelModalGo(1);
    if (e.key === 'ArrowLeft') reelModalGo(-1);
  });



  // --------------------------------------------------------------
  // Cart drawer upsell slider (single-card carousel)
  //
  // The drawer renders an empty <div data-eagleray-cart-recs data-product-id>
  // (snippets/header-actions.liquid). We fill it via Shopify's Product
  // Recommendations API, rendering sections/eagleray-cart-recommendations.liquid.
  // On any cart:update the drawer morphs (wiping/refreshing this container), so
  // we re-load after each update for the current first cart item.
  // Includes: slider engine, auto-play, touch/swipe, pagination dots,
  // single-variant AJAX add, multi-variant quick-add modal.
  // --------------------------------------------------------------
  var __erCartRecsBound = false;
  var __erUpsellLastPid = null;

  function initCartRecs(scope) {
    var root = rootOf(scope);
    var nodes = root.querySelectorAll ? root.querySelectorAll('[data-eagleray-cart-recs]') : [];
    Array.prototype.forEach.call(nodes, function (el) {
      if (notInited(el)) markInited(el);
      loadCartRecs(el);
    });
    bindCartRecsRefresh();
  }

  function loadCartRecs(el) {
    var pid = el.getAttribute('data-product-id');
    if (!pid) return;
    // skip re-fetch if the product hasn't changed (dedup)
    if (__erUpsellLastPid === pid && el.querySelector('[data-eagleray-upsell]')) return;
    __erUpsellLastPid = pid;
    var base = el.getAttribute('data-recs-url') || '/recommendations/products';
    var url = base + '?section_id=eagleray-cart-recommendations&limit=6&product_id=' + encodeURIComponent(pid);
    fetch(url)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) { el.innerHTML = ''; return; }
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var upsellRoot = tmp.querySelector('[data-eagleray-upsell]');
        el.innerHTML = upsellRoot ? upsellRoot.outerHTML : '';
        wireUpsellAdd(el);
        initUpsellSlider(el);
      })
      .catch(function () { });
  }

  // ---- Slider engine ----
  function initUpsellSlider(container) {
    var track = container.querySelector('[data-eagleray-upsell-track]');
    var dotsWrap = container.querySelector('[data-eagleray-upsell-dots]');
    if (!track) return;
    var slides = track.querySelectorAll('[data-eagleray-upsell-slide]');
    if (!slides.length) return;
    var total = slides.length;
    var current = 0;
    var autoplayId = null;
    var resumeId = null;
    var AUTOPLAY_MS = 4500;
    var RESUME_DELAY = 6000;
    var SWIPE_THRESHOLD = 30;

    function goTo(idx) {
      current = ((idx % total) + total) % total; // infinite loop
      track.style.transform = 'translateX(-' + (current * 100) + '%)';
      updateDots();
    }

    function next() { goTo(current + 1); }

    function updateDots() {
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('[data-eagleray-upsell-dot]');
      Array.prototype.forEach.call(dots, function (d, i) {
        d.classList.toggle('is-active', i === current);
      });
    }

    // ---- Auto-play ----
    function startAutoplay() {
      stopAutoplay();
      autoplayId = setInterval(next, AUTOPLAY_MS);
    }

    function stopAutoplay() {
      if (autoplayId) { clearInterval(autoplayId); autoplayId = null; }
      if (resumeId) { clearTimeout(resumeId); resumeId = null; }
    }

    function pauseAndScheduleResume() {
      stopAutoplay();
      resumeId = setTimeout(startAutoplay, RESUME_DELAY);
    }

    // ---- Dot clicks ----
    if (dotsWrap) {
      on(dotsWrap, 'click', function (e) {
        var dot = e.target.closest('[data-eagleray-upsell-dot]');
        if (!dot) return;
        var idx = parseInt(dot.getAttribute('data-eagleray-upsell-dot'), 10);
        if (!isNaN(idx)) { goTo(idx); pauseAndScheduleResume(); }
      });
    }

    // ---- Touch / swipe ----
    var touchStartX = 0;
    var touchDeltaX = 0;
    var isSwiping = false;

    track.addEventListener('touchstart', function (e) {
      var t = e.touches[0];
      if (!t) return;
      touchStartX = t.clientX;
      touchDeltaX = 0;
      isSwiping = true;
      track.classList.add('is-dragging');
      stopAutoplay();
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (!isSwiping) return;
      var t = e.touches[0];
      if (!t) return;
      touchDeltaX = t.clientX - touchStartX;
      // live drag feedback: shift the track by deltaX
      var offset = -(current * 100);
      var pxPct = (touchDeltaX / track.offsetWidth) * 100;
      track.style.transform = 'translateX(calc(' + offset + '% + ' + pxPct + '%))';
    }, { passive: true });

    track.addEventListener('touchend', function () {
      if (!isSwiping) return;
      isSwiping = false;
      track.classList.remove('is-dragging');
      if (Math.abs(touchDeltaX) > SWIPE_THRESHOLD) {
        if (touchDeltaX < 0) goTo(current + 1); // swipe left → next
        else goTo(current - 1); // swipe right → prev
      } else {
        goTo(current); // snap back
      }
      touchDeltaX = 0;
      pauseAndScheduleResume();
    }, { passive: true });

    // ---- Mouse hover pause (desktop) ----
    var viewport = container.querySelector('.eagleray-upsell__viewport') || container;
    on(viewport, 'mouseenter', function () { stopAutoplay(); });
    on(viewport, 'mouseleave', function () { startAutoplay(); });

    // ---- Boot ----
    goTo(0);
    if (total > 1) startAutoplay();
  }

  // ---- Add to cart logic ----
  function wireUpsellAdd(container) {
    // Single-variant: direct AJAX add
    Array.prototype.forEach.call(container.querySelectorAll('[data-er-upsell-add]'), function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.classList.contains('is-loading')) return;
        var vid = btn.getAttribute('data-variant-id');
        if (!vid) return;
        btn.classList.add('is-loading');
        btn.textContent = '...';
        cartRecsQuickAdd(vid, function () {
          btn.classList.remove('is-loading');
          btn.textContent = 'Add';
          // Force re-fetch recs on next cart:update (product may have changed)
          __erUpsellLastPid = null;
        });
      });
    });

    // Multi-variant: open quick-add modal
    Array.prototype.forEach.call(container.querySelectorAll('[data-er-upsell-quickadd]'), function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.classList.contains('is-loading')) return;
        var productUrl = btn.getAttribute('data-product-url');
        var productTitle = btn.getAttribute('data-product-title');
        if (!productUrl) return;
        btn.classList.add('is-loading');
        openQuickAddForProduct(productUrl, productTitle, function () {
          btn.classList.remove('is-loading');
        });
      });
    });
  }

  function openQuickAddForProduct(productUrl, productTitle, done) {
    // Fetch the product page, extract [data-product-grid-content], inject
    // into the theme's existing <quick-add-dialog> modal.
    fetch(productUrl)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) { if (done) done(); return; }
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var gridContent = doc.querySelector('[data-product-grid-content]');
        var modalContent = document.getElementById('quick-add-modal-content');
        var dialog = document.getElementById('quick-add-dialog');
        if (!gridContent || !modalContent || !dialog) {
          // Fallback: navigate to the product page
          window.location.href = productUrl;
          return;
        }

        var gridClone = gridContent.cloneNode(true);

        // ---- Mobile DOM Rearrangement ----
        // The base theme hides .product-details on mobile and relies on JS
        // to move the form components out. We replicate that here.
        if (window.matchMedia('(max-width: 749px)').matches) {
          var productDetails = gridClone.querySelector('.product-details');
          var productFormComponent = gridClone.querySelector('product-form-component');
          var variantPicker = gridClone.querySelector('variant-picker');
          var productPrice = gridClone.querySelector('product-price');

          var titleLink = document.createElement('a');
          titleLink.textContent = productTitle || '';
          titleLink.href = productUrl;

          var productHeader = document.createElement('div');
          productHeader.classList.add('product-header');
          productHeader.appendChild(titleLink);
          if (productPrice) productHeader.appendChild(productPrice);

          gridClone.appendChild(productHeader);
          if (variantPicker) gridClone.appendChild(variantPicker);
          if (productFormComponent) gridClone.appendChild(productFormComponent);

          if (productDetails) productDetails.remove();
        }
        // ----------------------------------

        modalContent.innerHTML = '';
        modalContent.appendChild(gridClone);

        // Ensure variant pickers re-initialize their logic
        var vPicker = modalContent.querySelector('variant-picker');
        if (vPicker && typeof vPicker.updateVariantPicker === 'function') {
          vPicker.updateVariantPicker(gridClone);
        }

        // Show the dialog using the existing web component method
        if (typeof dialog.showDialog === 'function') {
          dialog.showDialog();
        } else {
          var dlg = dialog.querySelector('dialog');
          if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
        }
        if (done) done();
      })
      .catch(function () {
        // Fallback: navigate to product
        window.location.href = productUrl;
        if (done) done();
      });
  }

  function cartRecsQuickAdd(variantId, done) {
    var cartItems = document.querySelector('cart-items-component');
    var sectionId = cartItems ? cartItems.dataset.sectionId : null;
    var body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (sectionId) { body.sections = sectionId; body.sections_url = window.location.pathname; }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status) { if (done) done(); return; } // e.g. sold out
        return fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'eagleray-cart-recs',
                data: { source: 'eagleray-cart-recs', sections: (res && res.sections) || {} }
              }
            }));
            if (done) done();
          });
      })
      .catch(function () { if (done) done(); });
  }

  function bindCartRecsRefresh() {
    if (__erCartRecsBound) return;
    __erCartRecsBound = true;
    document.addEventListener('cart:update', function () {
      // Clear the dedup cache so recs re-fetch after cart structural change
      __erUpsellLastPid = null;
      setTimeout(function () {
        Array.prototype.forEach.call(
          document.querySelectorAll('[data-eagleray-cart-recs]'),
          function (el) { loadCartRecs(el); }
        );
      }, 90);
    });
  }

  // --------------------------------------------------------------
  // Header cart count (snippets/header-actions.liquid → .eagleray-cart)
  //
  // Replaces cart-icon.js for the custom Eagle Ray cart button: keep
  // [data-eagleray-cart-count] in sync on every cart change. Reads the true
  // item_count from the cart:update resource (fallback /cart.js).
  // --------------------------------------------------------------
  var __erCartCountBound = false;
  var __erProgressBound = false;

  function setCartCount(n) {
    n = Number(n) || 0;
    Array.prototype.forEach.call(document.querySelectorAll('[data-eagleray-cart-count]'), function (el) {
      el.textContent = n > 99 ? '99+' : String(n);
      el.classList.toggle('is-empty', n <= 0);
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-eagleray-cart-a11y]'), function (el) {
      el.textContent = String(n);
    });
  }

  function refreshCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { if (c && typeof c.item_count === 'number') setCartCount(c.item_count); })
      .catch(function () { });
  }

  function initCartCount() {
    if (__erCartCountBound) return;
    __erCartCountBound = true;
    document.addEventListener('cart:update', function (e) {
      var res = e && e.detail && e.detail.resource;
      if (res && typeof res.item_count === 'number') setCartCount(res.item_count);
      else refreshCartCount();
    });
    // bfcache restore (back/forward) — cart may have changed elsewhere
    window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCartCount(); });
  }

  // --------------------------------------------------------------
  // Cart drawer progress bar (3 tiers, spend / quantity mode)
  // --------------------------------------------------------------
  function formatProgressRemaining(value, mode, currency) {
    if (mode === 'spend') {
      var v = value / 100;
      if (currency === 'INR') return '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return currency + ' ' + v.toFixed(2);
    }
    return value + (value === 1 ? ' item' : ' items');
  }

  function updateProgressBars() {
    var bars = document.querySelectorAll('[data-eagleray-cart-progress]');
    if (!bars.length) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart) return;
        bars.forEach(function (bar) {
          var mode = bar.getAttribute('data-mode');
          if (!mode) return;
          var current = mode === 'spend' ? (cart.original_total_price || cart.total_price || 0) : (cart.item_count || 0);
          var currency = bar.getAttribute('data-currency') || 'INR';
          var maxTarget = parseFloat(bar.getAttribute('data-max-target')) || 0;
          // data-max-target is already passed in cents by Liquid if mode is spend

          var tiers = [];
          for (var i = 1; i <= 3; i++) {
            var target = parseFloat(bar.getAttribute('data-t' + i + '-target')) || 0;
            var unlocking = bar.getAttribute('data-t' + i + '-unlocking') || '';
            var unlocked = bar.getAttribute('data-t' + i + '-unlocked') || '';
            if (target > 0) tiers.push({ target: target, unlocking: unlocking, unlocked: unlocked, index: i });
          }
          if (!tiers.length) return;
          tiers.sort(function (a, b) { return a.target - b.target; });

          var reachedIdx = -1;
          for (var i = 0; i < tiers.length; i++) {
            var tTarget = mode === 'spend' ? tiers[i].target * 100 : tiers[i].target;
            if (current >= tTarget) reachedIdx = i;
          }

          var msgEl = bar.querySelector('[data-eagleray-cart-progress-msg]');
          var fillEl = bar.querySelector('[data-eagleray-cart-progress-fill]');
          var message;

          function wrapProgressRemaining(str) {
            return '<span class="eagleray-cart-progress__highlight">' + str + '</span>';
          }

          if (reachedIdx === tiers.length - 1) {
            message = tiers[reachedIdx].unlocked;
          } else if (reachedIdx >= 0) {
            var next = tiers[reachedIdx + 1];
            var nextTarget = mode === 'spend' ? next.target * 100 : next.target;
            var remaining = Math.max(0, nextTarget - current);
            message = next.unlocking.replace(/\{\{remaining\}\}/g, wrapProgressRemaining(formatProgressRemaining(remaining, mode, currency)));
          } else {
            var first = tiers[0];
            var firstTarget = mode === 'spend' ? first.target * 100 : first.target;
            var remaining = Math.max(0, firstTarget - current);
            message = first.unlocking.replace(/\{\{remaining\}\}/g, wrapProgressRemaining(formatProgressRemaining(remaining, mode, currency)));
          }

          var pct = 0;
          if (tiers.length > 0) {
            if (current >= maxTarget) {
              pct = 100;
            } else {
              var segmentSize = 100 / tiers.length;
              var prevTarget = 0;
              for (var i = 0; i < tiers.length; i++) {
                var tierTarget = mode === 'spend' ? tiers[i].target * 100 : tiers[i].target;
                if (current <= tierTarget) {
                  var gap = tierTarget - prevTarget;
                  var rem = current - prevTarget;
                  var segmentProgress = gap > 0 ? (rem / gap) : 0;
                  pct = (i * segmentSize) + (segmentProgress * segmentSize);
                  break;
                }
                prevTarget = tierTarget;
              }
            }
          }

          if (msgEl) msgEl.innerHTML = message;
          if (fillEl) fillEl.style.width = pct + '%';

          var meterEl = bar.querySelector('[data-eagleray-cart-progress-meter]');
          if (meterEl) meterEl.setAttribute('aria-valuenow', String(Math.round(pct)));

          // Update milestone markers
          var markers = bar.querySelectorAll('[data-eagleray-cart-progress-marker]');
          var nextFound = false;
          markers.forEach(function (marker) {
            var mIdx = parseInt(marker.getAttribute('data-eagleray-cart-progress-marker'), 10);
            var tierTarget = parseFloat(bar.getAttribute('data-t' + mIdx + '-target')) || 0;
            var tTarget = mode === 'spend' ? tierTarget * 100 : tierTarget;
            var subEl = marker.querySelector('[data-eagleray-cart-progress-sub]');
            marker.classList.remove('is-next');
            if (current >= tTarget) {
              marker.classList.add('is-reached');
              if (subEl) subEl.textContent = 'Unlocked';
            } else {
              marker.classList.remove('is-reached');
              if (subEl) subEl.textContent = subEl.getAttribute('data-threshold') || '';
              if (!nextFound) {
                marker.classList.add('is-next');
                nextFound = true;
              }
            }
          });
        });
      })
      .catch(function () { });
  }

  function initCartProgress() {
    var bars = document.querySelectorAll('[data-eagleray-cart-progress]');
    bars.forEach(function (bar) { if (!bar.hasAttribute('data-eagleray-init')) bar.setAttribute('data-eagleray-init', ''); });
    updateProgressBars();
    if (__erProgressBound) return;
    __erProgressBound = true;
    document.addEventListener('cart:update', function () {
      requestAnimationFrame(updateProgressBars);
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) updateProgressBars(); });
  }

  // --------------------------------------------------------------
  // Wishlist (Phase 1: guest localStorage; Phase 2: logged-in sync)
  //
  // Storage: localStorage key 'eagleray:wishlist' → JSON array of
  //   { handle, id, addedAt }, newest first.
  // DOM hooks:
  //   [data-eagleray-wishlist-toggle]  save buttons (cards + PDP)
  //   [data-eagleray-wishlist-count]   header count badge(s) (+ -a11y mirror)
  //   [data-eagleray-wishlist-grid]    wishlist page grid container
  //   [data-eagleray-wishlist-remove]  remove (×) button on a wishlist card
  // Events: dispatches 'wishlist:update' { detail: { count, items } } and
  //   syncs across tabs via the native 'storage' event.
  //
  // The saved-card grid is filled with Shopify's Section Rendering API —
  // /products/{handle}?section_id=eagleray-wishlist-card — exactly like
  // initCartRecs() above, so prices/markup come straight from Liquid.
  //
  // ---- Phase 2 sync API contract (App Proxy; implemented later) ----
  //   Base: /apps/wishlist  (subpath chosen when the app is created)
  //   GET  /apps/wishlist  ->  { items: [{ handle, id }] }   (logged_in_customer_id)
  //   POST /apps/wishlist  body { action:'add'|'remove'|'set', handle, id, items }
  //                        ->  { items: [...] }              (canonical state)
  //   Backend verifies the App-Proxy HMAC, reads logged_in_customer_id, and
  //   reads/writes the customer metafield custom.wishlist (list.product_reference,
  //   GIDs derived from id) via Admin GraphQL metafieldsSet. On login: union
  //   the local + server lists, POST 'set', then treat server as source of truth.
  // --------------------------------------------------------------
  var WISHLIST_KEY = 'eagleray:wishlist';
  var WISHLIST_CARD_SECTION = 'eagleray-wishlist-card';
  var __erWishlistBound = false;

  // Phase 2 sync adapter — fully stubbed for Phase 1 (guest-only). Flip
  // enabled() to a real (logged-in && feature-flag) check and implement
  // pull/push/mergeOnLoad against the contract above when the backend exists.
  var WishlistSync = {
    enabled: function () { return false; },
    isLoggedIn: function () {
      var el = document.querySelector('[data-eagleray-logged-in]');
      return !!(el && el.getAttribute('data-eagleray-logged-in') === 'true');
    },
    pull: function () { return Promise.resolve(null); },
    push: function (/* items, change */) { },
    mergeOnLoad: function () { }
  };

  function wishlistNow() { try { return Date.now(); } catch (e) { return 0; } }

  function wishlistRead() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      return arr.filter(function (it) { return it && it.handle; });
    } catch (e) { return []; }
  }

  function wishlistPersist(items) {
    try { window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items)); } catch (e) { }
  }

  function wishlistDispatch(items) {
    document.dispatchEvent(new CustomEvent('wishlist:update', {
      bubbles: true,
      detail: { count: items.length, items: items }
    }));
  }

  function wishlistWrite(items, change) {
    wishlistPersist(items);
    wishlistDispatch(items);
    if (WishlistSync.enabled()) WishlistSync.push(items, change);
  }

  function wishlistHas(handle) {
    if (!handle) return false;
    return wishlistRead().some(function (it) { return it.handle === handle; });
  }

  function wishlistAdd(item) {
    if (!item || !item.handle) return;
    var items = wishlistRead();
    if (items.some(function (it) { return it.handle === item.handle; })) return;
    items.unshift({ handle: item.handle, id: item.id || null, addedAt: wishlistNow() });
    wishlistWrite(items, { action: 'add', handle: item.handle, id: item.id || null });
  }

  function wishlistRemove(handle) {
    if (!handle) return;
    var items = wishlistRead().filter(function (it) { return it.handle !== handle; });
    wishlistWrite(items, { action: 'remove', handle: handle });
  }

  function wishlistToggle(item) {
    if (!item || !item.handle) return false;
    if (wishlistHas(item.handle)) { wishlistRemove(item.handle); return false; }
    wishlistAdd(item);
    return true;
  }

  // ---- count badge (mirrors setCartCount/initCartCount) ----
  function setWishlistCount(n) {
    n = Number(n) || 0;
    Array.prototype.forEach.call(document.querySelectorAll('[data-eagleray-wishlist-count]'), function (el) {
      el.textContent = n > 99 ? '99+' : String(n);
      el.classList.toggle('is-empty', n <= 0);
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-eagleray-wishlist-a11y]'), function (el) {
      el.textContent = String(n);
    });
  }

  function initWishlistCount() {
    // Always refresh badges from the store (cheap; also covers editor re-renders).
    setWishlistCount(wishlistRead().length);
    if (__erWishlistBound) return;
    __erWishlistBound = true;

    document.addEventListener('wishlist:update', function (e) {
      var count = e && e.detail && typeof e.detail.count === 'number'
        ? e.detail.count
        : wishlistRead().length;
      setWishlistCount(count);
      reflectWishlistToggles(document);
    });
    // cross-tab: localStorage changes in another tab
    window.addEventListener('storage', function (e) {
      if (e && e.key && e.key !== WISHLIST_KEY) return;
      setWishlistCount(wishlistRead().length);
      reflectWishlistToggles(document);
    });
    // bfcache restore (back/forward)
    window.addEventListener('pageshow', function (e) { if (e.persisted) setWishlistCount(wishlistRead().length); });

    // Phase 2: pull server list + merge once (no-op while sync is disabled).
    WishlistSync.mergeOnLoad();
  }

  // ---- save toggles (cards + PDP) ----
  function reflectWishlistToggles(scope) {
    var saved = {};
    wishlistRead().forEach(function (it) { saved[it.handle] = true; });
    rootOf(scope).querySelectorAll('[data-eagleray-wishlist-toggle]').forEach(function (btn) {
      var on = !!saved[btn.getAttribute('data-handle')];
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = btn.querySelector('[data-eagleray-wishlist-toggle-label]');
      if (label) {
        label.textContent = on
          ? (btn.getAttribute('data-added-label') || 'Saved')
          : (btn.getAttribute('data-default-label') || 'Save to wishlist');
      }
    });
  }

  function initWishlistToggles(scope) {
    rootOf(scope).querySelectorAll('[data-eagleray-wishlist-toggle]').forEach(function (btn) {
      if (notInited(btn)) {
        markInited(btn);
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var idAttr = btn.getAttribute('data-product-id');
          wishlistToggle({
            handle: btn.getAttribute('data-handle'),
            id: idAttr ? Number(idAttr) : null,
            title: btn.getAttribute('data-title')
          });
          reflectWishlistToggles(document);
        });
      }
    });
    reflectWishlistToggles(scope);
  }

  // ---- wishlist page (Section Rendering API; mirrors loadCartRecs) ----
  function initWishlistPage(scope) {
    rootOf(scope).querySelectorAll('[data-eagleray-wishlist-grid]').forEach(function (grid) {
      if (!notInited(grid)) return;
      markInited(grid);
      renderWishlistPage(grid);
      // cross-tab edits should refresh the grid (same-tab edits update the DOM directly)
      window.addEventListener('storage', function (e) {
        if (e && e.key && e.key !== WISHLIST_KEY) return;
        renderWishlistPage(grid);
      });
    });
  }

  function wishlistPageParts(grid) {
    var section = grid.closest('[data-eagleray-wishlist]');
    return {
      section: section,
      loading: section ? section.querySelector('[data-eagleray-wishlist-loading]') : null,
      empty: section ? section.querySelector('[data-eagleray-wishlist-empty]') : null,
      cardSection: (section && section.getAttribute('data-card-section')) || WISHLIST_CARD_SECTION
    };
  }

  function renderWishlistPage(grid) {
    var parts = wishlistPageParts(grid);
    var items = wishlistRead();

    if (!items.length) {
      grid.innerHTML = '';
      if (parts.loading) parts.loading.setAttribute('hidden', '');
      if (parts.empty) parts.empty.removeAttribute('hidden');
      return;
    }
    if (parts.empty) parts.empty.setAttribute('hidden', '');
    if (parts.loading) parts.loading.removeAttribute('hidden');

    Promise.all(items.map(function (it) {
      return fetch('/products/' + encodeURIComponent(it.handle) + '?section_id=' + parts.cardSection, {
        headers: { Accept: 'text/html' }
      })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var node = null;
          if (html) {
            var tmp = document.createElement('div');
            tmp.innerHTML = html;
            node = tmp.querySelector('[data-eagleray-wishlist-card-root]');
          }
          return { handle: it.handle, node: node };
        })
        .catch(function () { return { handle: it.handle, node: null }; });
    })).then(function (results) {
      grid.innerHTML = '';
      var stale = [];
      results.forEach(function (res) {
        if (res.node) grid.appendChild(res.node);
        else stale.push(res.handle);
      });
      // Silently drop products that 404'd / are no longer available. Update the
      // store directly (no dispatch) so this render isn't re-triggered.
      if (stale.length) {
        var kept = wishlistRead().filter(function (it) { return stale.indexOf(it.handle) === -1; });
        wishlistPersist(kept);
        setWishlistCount(kept.length);
        reflectWishlistToggles(document);
      }
      if (parts.loading) parts.loading.setAttribute('hidden', '');
      if (!grid.children.length && parts.empty) parts.empty.removeAttribute('hidden');
      wireWishlistRemove(grid);
    });
  }

  function wireWishlistRemove(grid) {
    Array.prototype.forEach.call(grid.querySelectorAll('[data-eagleray-wishlist-remove]'), function (btn) {
      if (!notInited(btn)) return;
      markInited(btn);
      on(btn, 'click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var handle = btn.getAttribute('data-handle');
        var card = btn.closest('[data-eagleray-wishlist-card-root]');
        wishlistRemove(handle); // persists + dispatches → count/toggles update
        if (card) {
          card.classList.add('is-removing');
          setTimeout(function () {
            if (card.parentNode) card.parentNode.removeChild(card);
            var parts = wishlistPageParts(grid);
            if (!grid.children.length && parts.empty) parts.empty.removeAttribute('hidden');
          }, 220);
        }
      });
    });
  }

  // --------------------------------------------------------------
  // Boot + Shopify editor lifecycle
  // --------------------------------------------------------------
  function bootAll(scope) {
    initReveal(scope);
    initCarousels(scope);
    initHeroSlideshow(scope);
    initReelAutoplay(scope);
    initReelModal(scope);
    initNewsletter(scope);
    initAnnouncementOffset();
    initNavOffset();
    initHeader(scope);
    initCategoryNav(scope);
    initMegaMenu(scope);
    initCartRecs(scope);
    initCartCount();
    initCartProgress();
    initWishlistCount();
    initWishlistToggles(scope);
    initWishlistPage(scope);
  }

  function resetTransientState() {
    document.body.style.overflow = '';
    var menu = document.querySelector('[data-eagleray-mobile-menu]');
    var toggle = document.querySelector('[data-eagleray-menu-toggle]');
    if (menu && menu.classList.contains('is-open')) menu.classList.remove('is-open');
    if (toggle && toggle.classList.contains('is-active')) toggle.classList.remove('is-active');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootAll(); });
  } else {
    bootAll();
  }

  // Re-init scoped to the section the editor just rendered.
  document.addEventListener('shopify:section:load', function (e) { bootAll(e.target); });
  document.addEventListener('shopify:section:unload', function () { resetTransientState(); });
  document.addEventListener('shopify:section:select', function (e) { bootAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    bootAll(e.target);
    var slide = e.target && e.target.closest && e.target.closest('[data-eagleray-hero-slide]');
    if (!slide) return;
    var hero = slide.closest('[data-eagleray-hero-slideshow]');
    if (!hero || typeof hero.__eaglerayHeroShow !== 'function') return;
    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-eagleray-hero-slide]'));
    var idx = slides.indexOf(slide);
    if (idx >= 0) hero.__eaglerayHeroShow(idx);
  });
})();
